import api from './api';

// ── Types ─────────────────────────────────────────────────────────────────────

export type HealthSeverity = 'error' | 'warning' | 'info';

export interface HealthCheck {
  count:    number;
  severity: HealthSeverity;
  label:    string;
}

export interface HealthChecks {
  orphanedBalances:   HealthCheck;
  staleSupplierLinks: HealthCheck;
  stockNoSupplier:    HealthCheck;
  missingBalances:    HealthCheck;
  unusedItems:        HealthCheck;
  emptyWineLists:     HealthCheck;
  /** SIMPLE-WINE.4 §6 — WINE items with nothing in the catalogue behind them. */
  unlinkedWineItems?: HealthCheck;
}

export interface UnlinkedWineItem {
  _id: string;
  displayName: string;
  sizeValue: number | null;
  sizeUnit: string | null;
  createdAt: string | null;
}

/**
 * ITEM-COMPANY.1 — the report groups by COMPANY, not venue.
 *
 * An inventory item master belongs to a company; it names no venue. The API
 * used to group these rows by `inventory_items.venueId`, which meant "the venue
 * that first typed this product in" — not who can see it, and not where it is.
 */
export interface UnlinkedWineCompany {
  companyId: string;
  /** The company's venue names, joined — so a row stays recognisable. */
  companyName: string | null;
  /**
   * GOVERNANCE-LINK.1 — a venue to deep-link THROUGH, not a property of these
   * items. The detail page is venue-scoped and any venue in the company opens
   * it. Null when none resolved: do not build a URL from it.
   */
  linkVenueId: string | null;
  count: number;
  items: UnlinkedWineItem[];
}

export interface UnlinkedWineItemsResponse {
  total: number;
  companies: UnlinkedWineCompany[];
  generatedAt: string;
}

export interface HealthSummaryResponse {
  checks:      HealthChecks;
  generatedAt: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

const healthService = {
  getSummary: async (): Promise<HealthSummaryResponse> => {
    const res = await api.get<HealthSummaryResponse>('/admin/governance/health');
    return res.data;
  },

  /**
   * SIMPLE-WINE.4 §6 — the standing report behind the count. Grouped by
   * venue, because the person who knows what bottle is on the shelf is there
   * and not in governance.
   */
  getUnlinkedWineItems: async (venueId?: string): Promise<UnlinkedWineItemsResponse> => {
    const res = await api.get<UnlinkedWineItemsResponse>(
      '/admin/governance/health/unlinked-wine-items',
      { params: venueId ? { venueId } : {} },
    );
    return res.data;
  },
};

export default healthService;
